import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { api, ApiError } from "./api.js";
import { createOnboardingReader, type OnboardingState } from "./onboarding-state.js";

interface AuthUser {
  id: string;
  email: string;
  name: string | null;
  planTier: "free" | "paid";
  isAdmin: boolean;
  createdAt: string;
}

interface AuthState {
  status: "loading" | "signed-in" | "signed-out";
  user: AuthUser | null;
  onboarding: OnboardingState;
  refresh: () => Promise<void>;
  refreshOnboarding: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthState["status"]>("loading");
  const [onboarding, setOnboarding] = useState<OnboardingState>({ status: "pending" });
  const [onboardingReader] = useState(() => createOnboardingReader(
    () => api.get<{ onboardedAt: string | null }>("/auth/me/onboarding"),
    setOnboarding,
  ));
  const sessionRevision = useRef(0);
  const signedIn = useRef(false);

  const refreshOnboarding = async () => {
    if (signedIn.current) await onboardingReader.refresh();
  };

  const refresh = async () => {
    const current = ++sessionRevision.current;
    signedIn.current = false;
    onboardingReader.reset();
    try {
      const res = await api.get<{ user: AuthUser }>("/auth/me");
      if (current !== sessionRevision.current) return;
      signedIn.current = true;
      setUser(res.user);
      setStatus("signed-in");
      await refreshOnboarding();
    } catch (err) {
      if (current !== sessionRevision.current) return;
      if (err instanceof ApiError && err.status === 401) {
        setUser(null);
        setStatus("signed-out");
      } else {
        throw err;
      }
    }
  };

  const signOut = async () => {
    sessionRevision.current += 1;
    signedIn.current = false;
    onboardingReader.reset();
    await api.post("/auth/logout");
    setUser(null);
    setStatus("signed-out");
  };

  useEffect(() => {
    refresh().catch(() => setStatus("signed-out"));
    return () => {
      sessionRevision.current += 1;
      signedIn.current = false;
      onboardingReader.reset();
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{ status, user, onboarding, refresh, refreshOnboarding, signOut }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthState => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
};
